import { HeatingOrCoolingSource } from '../../../generated/graphql'

/**
 * Basis for heat pump calculations
 *
 * Assuming (ideal) pump power Pe (electricity), heating power Ph and cooling power Pc we can derive:
 * Ph := Pe + Pc
 * Pe = Ph - Pc
 * Pc = Ph - Pe
 *
 * For heating coefficient of performance we have:
 *
 * COP := Ph / Pe = 1 - Pc / Pe = Ph / (Ph - Pc)
 *
 * For cooling energy efficiency ratio we have:
 *
 * EER := Pc / Pe = Ph / Pe - 1 = Pc / (Ph - Pc)
 *
 * Pump power:
 *
 * Pe = Ph / COP
 *
 * Pe = Pc / EER
 *
 * Cooling power:
 *
 * Pc = EER * Pe
 *
 * Pc = Ph * (1 - 1 / COP)
 *
 * Note: ":=" denotes what is used as a definition.
 */

export type HeatingHeatPumpResult = {
  totalDemand: number
  providedHeating: number
  cop: number
}

export type HeatingHeatPumpResults = {
  basic: HeatingHeatPumpResult
  dhw: HeatingHeatPumpResult
}

export type CoolingHeatPumpResult = {
  providedCooling: number
  heatToSource: {
    type: HeatingOrCoolingSource
    amount: number
  }
  eer: number
}

/**
 * Get electricity consumption based on produced power and the conversion coefficient.
 *
 * Pe = Ph / COP (heating)
 * Pe = Pc / EER (cooling)
 *
 * @param power Produced heating (Ph) or cooling power (Pc)
 * @param coefficient COP for heating and EER for cooling
 */
const getElectricity = (power: number, coefficient: number) =>
  Number.isNaN(coefficient) ? 0 : power / coefficient

/** Pe = Pc / EER */
export function getElectricityBasedOnCooling({
  providedCooling,
  eer,
}: CoolingHeatPumpResult): number {
  return getElectricity(providedCooling, eer)
}

/** Pe = Ph / COP */
export function getElectricityBasedOnHeating({
  providedHeating,
  cop,
}: HeatingHeatPumpResult): number {
  return getElectricity(providedHeating, cop)
}

/** Pc = Ph * (1 - 1 / COP) */
export function getCoolingBasedOnHeating({
  providedHeating,
  cop,
}: HeatingHeatPumpResult): number {
  return Number.isNaN(cop) ? 0 : providedHeating * (1 - 1 / cop)
}

/**
 * EER = Pc / Pe
 * @param cooling Cooling power (Pc)
 * @param electricity Pump power (Pe)
 */
export function getEer(cooling: number, electricity: number): number {
  return electricity === 0 ? NaN : cooling / electricity
}

/**
 * @param usedPower The actual power produced by the heating or cooling circuit
 * @param power The heating or cooling power
 * @param powerCoefficient Power coefficient from performance table
 */
export function calculateUsedCapacity(
  usedPower: number,
  power: number,
  powerCoefficient: number,
): number {
  return usedPower / (power * powerCoefficient)
}
